import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router'

import { skipToken } from '@reduxjs/toolkit/query'

import { TransferStatus } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'

import { CLIENT_END_OF_SESSION, CLIENT_NOTES, LC_CALENDAR_CURRENT } from '../../../common/constants/routingConstants'
import { useAppDispatch } from '../../../data/store'
import { usePreviousRoute } from '../../../hooks/usePreviousRoute'
import { useGetTransfersQuery } from '../../../payments/data/transfersApi'
import { setToastContent } from '../../data/ltToastAutoActions'
import { isReverseChargeEligibleNote } from '../utils/reversedChargeNoteUtils'

interface UseReversedChargeCheckParams {
  noteName: string | undefined
  appointmentId: number | null | undefined
  clientId: string | null
  providerId: string
}

interface UseReversedChargeCheckResult {
  isReversedChargeNote: boolean
  isLoading: boolean
}

/**
 * Hook to check if a DA note has a reversed charge associated with it.
 * For DA notes, this hook will:
 * - Make an API call to check for reversed charges
 * - Handle errors by showing a toast and navigating back
 * - Return loading state and whether the note has a reversed charge
 *
 * For non-DA notes, this hook will return immediately with isLoading: false and isReversedChargeNote: false
 */
export const useReversedChargeCheck = ({
  noteName,
  appointmentId,
  clientId,
  providerId,
}: UseReversedChargeCheckParams): UseReversedChargeCheckResult => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const prevRoute = usePreviousRoute()

  const isDANote = isReverseChargeEligibleNote(noteName)

  const {
    data: reversedChargesData,
    isLoading: isLoadingReversedCharges,
    isError: isReversedChargesError,
  } = useGetTransfersQuery(
    isDANote && clientId && appointmentId
      ? {
          provider_id: providerId,
          patient_id: clientId,
          appointment_id: appointmentId,
          status: TransferStatus.REVERSED,
          showAlert: false,
        }
      : skipToken,
  )

  useEffect(() => {
    if (isReversedChargesError) {
      dispatch(
        setToastContent({
          text: formatMessage({
            defaultMessage: 'Unable to load note. Please try again.',
            description: 'Error message when reversed charge check fails for a note',
          }),
          id: 'ClientNoteDetails-reversed-charge-check-error',
          toastType: 'error',
        }),
      )
      if (
        prevRoute &&
        (prevRoute.includes(CLIENT_END_OF_SESSION.route) || prevRoute.includes(LC_CALENDAR_CURRENT.route))
      ) {
        navigate(LC_CALENDAR_CURRENT.route)
      } else {
        navigate(CLIENT_NOTES.route)
      }
    }
  }, [isReversedChargesError, dispatch, formatMessage, navigate, prevRoute])

  const isReversedChargeNote = Boolean(reversedChargesData?.data && reversedChargesData.data.length > 0)

  return {
    isReversedChargeNote,
    isLoading: isDANote && isLoadingReversedCharges,
  }
}
